/**
 * Native Google Sign-In (Capacitor) → Supabase session.
 *
 * Google refuses its OAuth consent screen inside a WebView, so the web
 * redirect flow (signInWithOAuth) can't complete in the native app. Instead we
 * use the platform's native account picker via @capgo/capacitor-social-login,
 * get a Google ID token, and exchange it for a Supabase session with
 * supabase.auth.signInWithIdToken — no browser, no redirect.
 *
 * Requires REACT_APP_GOOGLE_WEB_CLIENT_ID (the *Web* OAuth client ID Supabase
 * uses for Google). The token's audience is that web client; Supabase must list
 * it (and the Android client ID) under the Google provider's Authorized Client
 * IDs. The device also needs an Android OAuth client registered in Google Cloud
 * for package `app.wayshare` + the app's signing SHA-1.
 */
import { Capacitor } from '@capacitor/core';
import { supabase } from '../components/data/supabaseClient';

const WEB_CLIENT_ID = process.env.REACT_APP_GOOGLE_WEB_CLIENT_ID;

let initPromise = null;

/** True only when we're in the native app AND the web client ID is configured. */
export function isNativeGoogleAvailable() {
  return (
    typeof Capacitor?.isNativePlatform === 'function' &&
    Capacitor.isNativePlatform() &&
    !!WEB_CLIENT_ID
  );
}

async function ensureInitialized() {
  if (!initPromise) {
    initPromise = (async () => {
      const { SocialLogin } = await import('@capgo/capacitor-social-login');
      await SocialLogin.initialize({
        google: { webClientId: WEB_CLIENT_ID, mode: 'online' },
      });
      return SocialLogin;
    })().catch((err) => {
      // Reset so a later attempt can retry a transient init failure.
      initPromise = null;
      throw err;
    });
  }
  return initPromise;
}

/**
 * Show the native Google account picker and sign the user into Supabase.
 * Resolves with the Supabase session data, or throws on error/cancel.
 */
export async function signInWithGoogleNative() {
  const SocialLogin = await ensureInitialized();

  // Authentication only: no custom scopes. Passing scopes triggers the plugin's
  // authorization flow, which requires native MainActivity changes and throws
  // "You CANNOT use scopes without modifying the main activity". The default
  // Credential Manager sign-in already returns an ID token with email + profile.
  const res = await SocialLogin.login({
    provider: 'google',
    options: {},
  });

  const idToken = res && res.result && res.result.idToken;
  if (!idToken) {
    throw new Error('Google did not return an ID token');
  }

  const { data, error } = await supabase.auth.signInWithIdToken({
    provider: 'google',
    token: idToken,
  });
  if (error) throw error;
  return data;
}
